import { getExtension, getFilenameFromPath } from '@/utils/pathUtils';
import { imageToCanvas } from '@/utils/imageUtils';
import { createCanvas, canvasToBlob, blobToBase64 } from '@/utils/canvasUtils';

(function () {
    'ngInject';

    angular.module('app.shared.services').service('ImageUtilService', imageUtilService);

    function imageUtilService($q, ImageAPIService, blockUI, $mdDialog) {
        /**
         * Reads and limits the image resolution from a Javascript File object maintaining
         *  the aspect ratio.
         * @param  {File}       file        File object from where to read the image
         * @param  {Number}     width       New image width
         * @param  {Number}     height      New image height
         * @return {Promise}    Returns a promise with the new File object as result
         */
        function limitImageResolutionFromFile(file, width, height) {
            return $q(function (resolve, reject) {
                // Create a file reader
                const reader = new FileReader();
                reader.onload = function (e) {
                    const img = new Image();
                    img.onload = async function () {
                        // Limit the image resolution and convert it back to a file
                        const { _, canvas, resized } = await limitImageResolution(img, width, height);
                        canvas.toBlob(function (blob) {
                            const resizedFile = new File([blob], file.name, { type: file.type });
                            resolve({
                                file: resizedFile,
                                resized: resized
                            });
                        });
                    };
                    img.onerror = function (errorMsg, url, lineNumber, column, error) {
                        reject(error);
                    };
                    img.crossOrigin = 'Anonymous';
                    img.src = e.target.result;
                };
                reader.readAsDataURL(file);
            });
        }

        /**
         * Limits the image resolution from a Javascript Image object maintaining
         *  the aspect ratio.
         * @param  {HTMLImageElement | HTMLCanvasElement | string}  img         Image object to resize
         * @param  {Number}                                         width       New image width
         * @param  {Number}                                         height      New image height
         * @return {Promise} Returns a promise with the new resized Image as result
         */
        function limitImageResolution(img, width, height) {
            const MAX_WIDTH = width;
            const MAX_HEIGHT = height;

            return $q(async function (resolve, reject) {
                img = await imageToCanvas(img);
                let width = img.width;
                let height = img.height;
                let wasResized = false;
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Resize image if it's too big
                if (height > MAX_HEIGHT || width > MAX_WIDTH) {
                    wasResized = true;

                    let ratio = 1;
                    if (height > width) {
                        ratio = height / MAX_HEIGHT;
                    } else if (width > height) {
                        ratio = width / MAX_WIDTH;
                    }
                    // Width and height are the same
                    else {
                        ratio = width / MAX_WIDTH;
                    }

                    height /= ratio;
                    width /= ratio;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.imageSmoothingQuality = 'high';
                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(img, 0, 0, width, height);

                const dataURL = canvas.toDataURL();
                resolve({ base64: dataURL, canvas: canvas, resized: wasResized });
            });
        }

        function imageToBase64(image, format) {
            return $q(async function (resolve, reject) {
                try {
                    var canvas = createCanvas(image.width, image.height);

                    var context = canvas.getContext('2d');
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);
                    const blob = await canvasToBlob(canvas, `image/${format}`);
                    const base64Image = await blobToBase64(blob);

                    resolve(base64Image);
                } catch (error) {
                    reject(error);
                }
            });
        }

        function getImageFromSrc(imgSrc) {
            return $q(function (resolve, reject) {
                const imgLoader = new Image();
                imgLoader.onload = function () {
                    resolve(imgLoader);
                };
                imgLoader.onerror = function (errorMsg, url, lineNumber, column, error) {
                    reject(error);
                };
                imgLoader.crossOrigin = 'Anonymous';
                imgLoader.src = imgSrc;
            });
        }

        /**
         * Check if the image is just black opaque pixels
         * @param {HTMLImageElement|HTMLCanvasElement} image image to check
         */
        function isFullMask(image) {
            const { width, height } = image;
            const canvas = createCanvas(width, height);
            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);

            const imageData = ctx.getImageData(0, 0, image.width, image.height);
            const pixels = imageData.data;

            for (var n = 0; n < pixels.length; n += 4) {
                const r = pixels[n],
                    g = pixels[n + 1],
                    b = pixels[n + 2],
                    alpha = pixels[n + 3];
                if (r !== 0 || g !== 0 || b !== 0 || alpha !== 255) {
                    return false;
                }
            }

            return true;
        }

        function limitMaskImagesSize(prodImagePlaceholders, previewImagePlaceholders, maxImageSize) {
            return new Promise(async (resolve, reject) => {
                try {
                    const filenames = prodImagePlaceholders
                        .map((i) => i.maskPath && getFilenameFromPath(i.maskPath))
                        .filter((filename) => !!filename && getExtension(filename).toLowerCase() !== 'svg');
                    const blockUiMessage = blockUI.message();
                    const isBlockUiActive = blockUI.state().blockCount > 0;

                    // Check if images need resizing
                    blockUI.start('Checking mask images');
                    const needResizing = await ImageAPIService.doMaskImagesNeedResizing(filenames, maxImageSize);
                    blockUI.stop();

                    // We need to resize the images, alert the user
                    if (needResizing) {
                        // Stop blockUI to be able to show the modal
                        blockUI.stop();
                        const confirm = $mdDialog
                            .confirm()
                            .title('Large Mask Images')
                            .textContent(
                                'There are large mask images loaded into Image Placeholder elements that will result in bad performance and slow loading times on your store, do you want to resize them to an optimal size?'
                            )
                            .ok('Yes')
                            .cancel('No');

                        try {
                            // Ask user for images resize
                            await $mdDialog.show(confirm);

                            try {
                                // Resize dynamic images
                                blockUI.start('Resizing mask images!');
                                const response = await ImageAPIService.resizeMaskImages(filenames, maxImageSize);

                                // Apply the new images to the elements
                                for (const previewImage of previewImagePlaceholders.filter((i) => i.maskPath)) {
                                    // Find the image in the renamed images
                                    const renamedImage = response.find(
                                        (i) => i.OldFilename === getFilenameFromPath(previewImage.maskPath)
                                    );
                                    if (renamedImage) {
                                        // Replace the filename with the new one
                                        const newPath = previewImage.maskPath.replace(
                                            /(.*)\/.*/i,
                                            `$1/${renamedImage.NewFilename}`
                                        );
                                        previewImage.maskPath = newPath;
                                    }
                                }
                                blockUI.stop();
                            } catch (error) {
                                // Don't resize images
                                blockUI.stop();
                                console.error('Error resizing mask images', error);
                                reject('Error resizing mask images');
                                return;
                            }
                        } catch (_) {
                            // Don't resize images
                        } finally {
                            // Restore blockUi
                            if (isBlockUiActive) {
                                blockUI.start(blockUiMessage);
                            }
                        }
                    }

                    resolve();
                } catch (error) {
                    console.error('Error checking for mask images', error);
                    reject(error);
                }
            });
        }

        return {
            limitImageResolutionFromFile: limitImageResolutionFromFile,
            limitImageResolution: limitImageResolution,
            imageToBase64: imageToBase64,
            getImageFromSrc: getImageFromSrc,
            isFullMask: isFullMask,
            limitMaskImagesSize: limitMaskImagesSize
        };
    }
})();
